import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  IGetListRouteParams,
  IUpdateTripDetailParams,
  QUERY_ADD_ROUTE,
  QUERY_ADD_ROUTE_AREA,
  QUERY_DELETE_ROUTE_AREA,
  QUERY_UPDATE_ROUTE_BY_ID,
  QUERY_UPDATE_TRIP_DETAIL,
  VARIABLES_ADD_ROUTE,
  VARIABLES_ADD_ROUTE_AREA,
  VARIABLES_DELETE_ROUTE_AREA,
  VARIABLES_UPDATE_ROUTE,
  VARIABLE_UPDATE_TRIP_DETAIL,
} from 'apis/GraphQL/Queries/saleLineQuery';
import { ITripDetailParams } from 'apis/GraphQL/Variables/trip';
import { SaleLineApi } from 'apis/SaleLineApi';
import TripAPI from 'apis/TripApis';
import { Button, ButtonFilter } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import ButtonImport from 'components/Button/ButtonImport';
import { UploadModal } from 'components/UploadModal_V2';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import {
  ISaleLineForm,
  ISaleLineItemData,
  TSaleLineFormParams,
} from 'interfaces/SaleLine';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { toastError, toastSuccess } from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { IMPORT_ROUTE } from 'utils/constants/importId';
import DrawerSaleLinePage from './DrawerSaleLinePage';
import FilterSaleLinePage from './FilterSaleLinePage';
import ModalSaleLinePage, { optionStatus } from './ModalSaleLinePage';
import TableSaleLinePage from './TableSaleLinePage';

export interface ISaleLineData {
  items: ISaleLineItemData[];
  pageInfo?: any;
}

interface IHandleDelete {
  id: string;
  isCreate: boolean;
  data: ISaleLineItemData;
  fieldValues: ISaleLineForm;
}

const { PlusOutlined } = icons;

const initData: ISaleLineItemData[] = [
  {
    routeId: '',
    routeName: '',
    tripDetailId: '',
    numExpectedDays: '',
    statusId: 'R100_01',
    validFromDate: dayjs(new Date()),
    validToDate: '',
    tripId: '',
    tripName: '',
    indexNum: '',
    area: [],
    cityName: '',
    companyId: '',
    createDate: '',
    createUser: '',
    districtName: '',
    modifyDate: '',
    modifyUser: '',
    plantId: '',
    wardName: '',
  },
];

const initSaleLineData: ISaleLineData = {
  items: [],
};

const SaleLinePage = () => {
  const navigate = useNavigate();
  const childRef = useRef<any>(null);
  const filterRef = useRef<any>(null);

  const { user } = useUser(),
    { setTitle, setBreadcrumb } = useTitle();

  const [editIndex, setEditIndex] = useState(-1),
    [isOpenModal, setIsOpenModal] = useState(false),
    [isOpenFilter, setIsOpenFilter] = useState(false),
    [isOpenDrawer, setIsOpenDrawer] = useState(false),
    [isOpenImportModal, setIsOpenImportModal] = useState(false);

  const [formParams, setFormParams] = useState<any>({}),
    [drawerData, setDrawerData] = useState<any>([]),
    [data, setData] = useState<ISaleLineData>(initSaleLineData);

  const defaultFilter = useMemo(() => {
    if (user?.isSale || user?.isCN)
      return {
        plantId: (user as any)?.plantCode,
      };

    if (user?.isAdmin || user?.isHO) return {};
  }, [user]);

  const queryParam = useQueryParam(),
    page = parseInt(queryParam.get('page') + '') || 1,
    page_size = parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const { isPending: isRouteLoading, mutate: getListRoute } = useMutation({
    mutationFn: (params: IGetListRouteParams) =>
      SaleLineApi.getListRoute(params),
    onSuccess: (res, variables) => {
      const data = res.data?.getRouteList;
      if (!data?.items?.length) {
        setData(initSaleLineData);
        return;
      }

      if (variables.type === 'export') {
        const exportData = data.items.map((item: any) => {
          const active = optionStatus.find(
            (option) => option.value === item.statusId,
          )?.label;

          return {
            ...item,
            statusId: active,
            validFromDate:
              item.validFromDate &&
              `${dayjs(item.validFromDate).format('YYYY-MM-DD')}`,
            validToDate:
              item?.validToDate &&
              `${dayjs(item?.validToDate).format('YYYY-MM-DD')}`,
          };
        });

        exportExcel_v2(
          variables.headerRow,
          exportData || [],
          'Kế hoạch - Tuyến bán hàng',
        );
      } else
        setData({
          items: data.items,
          pageInfo: data.pageInfo,
        });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutateAsync: checkExistedTrip } = useMutation({
    mutationFn: (routeId: string) => TripAPI.checkExistedTrip(routeId),
    onError: (error) => console.log(error),
  });

  const { isPending: createRouteLoading, mutateAsync: createRouteMain } =
      useMutation({
        mutationFn: (params: any) =>
          axiosGraphQL.post('/graphql', {
            query: QUERY_ADD_ROUTE,
            variables: VARIABLES_ADD_ROUTE(params),
          }),
        onError: (error) => {
          console.log(error);
        },
      }),
    { isPending: createTripDetailLoading, mutateAsync: createTripDetail } =
      useMutation({
        mutationFn: (params: ITripDetailParams) =>
          TripAPI.createTripDetail(params),
        onError: (error) => {
          console.log(error);
        },
      });

  const { isPending: updateRouteMainLoading, mutateAsync: updateRouteMain } =
      useMutation({
        mutationFn: (params: any) =>
          axiosGraphQL.post('/graphql', {
            query: QUERY_UPDATE_ROUTE_BY_ID,
            variables: VARIABLES_UPDATE_ROUTE(params),
          }),
        onError: (error) => {
          console.log(error);
        },
      }),
    { isPending: updateTripDetailLoading, mutateAsync: updateTripDetail } =
      useMutation({
        mutationFn: (params: IUpdateTripDetailParams) =>
          axiosGraphQL.post('/graphql', {
            query: QUERY_UPDATE_TRIP_DETAIL,
            variables: VARIABLE_UPDATE_TRIP_DETAIL(params),
          }),
        onSuccess: () => {
          setEditIndex(-1);
        },
        onError: (error) => {
          console.log(error);
        },
      });

  const { isPending: createRouteAreaLoading, mutateAsync: createRouteArea } =
      useMutation({
        mutationFn: (params: any) =>
          axiosGraphQL.post('/graphql', {
            query: QUERY_ADD_ROUTE_AREA,
            variables: VARIABLES_ADD_ROUTE_AREA(params),
          }),
        onSuccess: () => {
          setIsOpenModal(false);
        },
        onError: (error) => {
          console.log(error);
        },
      }),
    { isPending: deleteRouteAreaLoading, mutateAsync: deleteRouteArea } =
      useMutation({
        mutationFn: (id: string) =>
          axiosGraphQL.post('/graphql', {
            query: QUERY_DELETE_ROUTE_AREA,
            variables: VARIABLES_DELETE_ROUTE_AREA(id),
          }),
        onError: (error) => {
          console.log(error);
        },
      });

  const refetchListRoute = useCallback(
    async (isExport?: boolean, headerRow?: any) => {
      const plantId = formParams?.plantId || defaultFilter?.plantId || '5000';

      const params: IGetListRouteParams = {
        page: isExport ? 1 : page,
        pageSize: isExport ? data.pageInfo?.totalItems || 1000 : page_size,
        ...(formParams && formParams),
        plantId: plantId,
        ...(isExport && {
          headerRow,
          type: 'export',
        }),
      };

      getListRoute(params);
    },
    [page, page_size, defaultFilter, formParams],
  );

  useEffect(() => {
    setTitle('Tuyến bán hàng');
    setBreadcrumb([{ title: 'Kế hoạch' }, { title: 'Tuyến bán hàng' }]);
  }, [setBreadcrumb, setTitle]);

  useEffect(() => {
    refetchListRoute();
  }, [refetchListRoute]);

  const handleExportExcel = async () => {
    if (childRef && childRef.current) {
      childRef.current.handleExportExcel();
    }
  };

  const handleImportData = useCallback(() => {
    setIsOpenImportModal(true);
  }, []);

  const handleRouteArea = useCallback(async (params: IHandleDelete) => {
    const { id, isCreate, data, fieldValues } = params;

    const paramsDeleteArea: any[] =
        data && data.area.length
          ? data.area.filter(
              (area: any) =>
                !fieldValues.area.some((item) => item.id === area.id),
            )
          : [],
      paramsInsertArea = isCreate
        ? fieldValues.area
        : fieldValues.area.filter((area) => !area.id);

    if (paramsInsertArea.length)
      await createRouteArea({ data: paramsInsertArea, id: id });

    if (paramsDeleteArea)
      await Promise.all(
        paramsDeleteArea.map((area) => deleteRouteArea(area.id)),
      );
  }, []);

  const handleClearFilter = useCallback(() => {
    if (!filterRef) return;
    filterRef?.current?.handleCancel();
  }, [filterRef]);

  const handleModalSubmit = async (
    fieldsValue: ISaleLineItemData,
    isCreateTrip: boolean,
  ) => {
    const isCreate = editIndex === -1,
      dataIndex = data.items[editIndex],
      message = isCreate ? 'Tạo tuyến thành công' : 'Cập nhật tuyến thành công';

    const params = {
      id: fieldsValue.routeId,
      plantId: user?.plantCode,
      name: fieldsValue.routeName,
      statusId: fieldsValue.statusId,
      validFromDate: fieldsValue.validFromDate,
      validToDate: fieldsValue.validToDate,
      numExpectedDays: fieldsValue.numExpectedDays.toString(),
    };

    let id;
    if (isCreate) {
      const response: any = await createRouteMain(params);
      if (response.errors) {
        toastError(`Tạo tuyến thất bại: ${response.errors[0].message}`);
        return;
      }

      id = response.data.createRouteM.id;
    } else {
      const today = dayjs(new Date().toISOString());
      const { plantId, statusId, ...others } = params;

      const response: any = await updateRouteMain({
        ...others,
        modifyId: user?.id,
        statusId: statusId,
        validToDate: statusId === 'R100_02' ? today : null,
      });

      if (response.errors) {
        toastError(`Cập nhật tuyến thất bại: ${response.errors[0].message}`);
        return;
      }

      id = response.data.updateRouteM.id;
    }

    await handleRouteArea({
      id,
      isCreate,
      data: dataIndex,
      fieldValues: fieldsValue,
    });

    if (isCreateTrip && fieldsValue.tripId) {
      const tripParams = {
          routeId: id,
          tripId: fieldsValue.tripId,
          indexNum: Number(fieldsValue.indexNum),
        },
        isTripExisted =
          (await checkExistedTrip(id)).data?.getTripDetail?.items.length > 0;

      isTripExisted
        ? await updateTripDetail({ ...tripParams, id: dataIndex.tripDetailId })
        : await createTripDetail(tripParams);
    }

    isCreate && navigate(`?page=1&page_size=${page_size}`);

    setEditIndex(-1);
    setIsOpenModal(false);

    if (isCreate && filterRef) handleClearFilter();

    await refetchListRoute();
    toastSuccess(message);
  };

  const handleOkFilter = useCallback(
    (values: any) => {
      navigate(`?page=1&page_size=${page_size}`);
      const { cityNewId, wardNewId } = values;

      const cities =
          cityNewId && cityNewId.length > 0 ? cityNewId.join(',') : undefined,
        wards =
          wardNewId && wardNewId.length > 0
            ? wardNewId.map((ward: any) => ward.split('-')[1]).join(',')
            : undefined,
        [customerId, _] = (values?.customer ?? '')?.split('-');

      const params = {
        ...values,
        cityId: cities,
        wardId: wards,
        name: values.name?.trim(),
        customer: customerId,
      };

      setFormParams(params);
    },
    [page_size],
  );

  const handleCancelFilter = useCallback(() => {
    setFormParams({});
  }, []);

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter isOpen={isOpenFilter} setIsOpen={setIsOpenFilter} />
        </Col>
        <Col>
          <ButtonExport onClick={handleExportExcel} />
        </Col>
        <Col>
          <ButtonImport onClick={handleImportData} />
        </Col>
        {user?.isAdmin || (
          <Col>
            <Button
              fill
              title="Tạo"
              iconBefore={<PlusOutlined />}
              handleOnclick={() => setIsOpenModal(true)}
            />
          </Col>
        )}
      </Row>
      <FilterSaleLinePage
        ref={filterRef}
        isOpen={isOpenFilter}
        defaultFilter={defaultFilter}
        onFinish={handleOkFilter}
        onCancel={handleCancelFilter}
      />
      <TableSaleLinePage
        data={data}
        ref={childRef}
        loading={isRouteLoading}
        setEditIndex={setEditIndex}
        refetch={refetchListRoute}
        setDrawerData={setDrawerData}
        setOpenModal={setIsOpenModal}
        setOpenDrawer={setIsOpenDrawer}
      />
      <UploadModal
        importId={IMPORT_ROUTE}
        templateFileName="Kế hoạch - Tuyến bán hàng - Template"
        open={isOpenImportModal}
        title={'Thêm tuyến bán hàng'}
        refetch={() => {
          handleClearFilter();
          refetchListRoute();
        }}
        setOpen={setIsOpenImportModal}
      />
      <ModalSaleLinePage
        open={isOpenModal}
        isEdit={editIndex !== -1}
        loading={
          createRouteLoading ||
          createRouteAreaLoading ||
          createTripDetailLoading ||
          deleteRouteAreaLoading ||
          updateRouteMainLoading ||
          updateTripDetailLoading
        }
        title={editIndex !== -1 ? 'Chỉnh sửa tuyến' : 'Tạo tuyến'}
        data={editIndex !== -1 ? [data.items[editIndex]] : initData}
        onSubmit={handleModalSubmit}
        setEditIndex={setEditIndex}
        setIsOpenModal={setIsOpenModal}
      />
      <DrawerSaleLinePage
        open={isOpenDrawer}
        recordId={drawerData.routeId}
        setOpen={setIsOpenDrawer}
      />
    </>
  );
};

export default SaleLinePage;
